/**
 * Settings tab registry for the admin view.
 *
 * The tab strip used to be eight hand-written triggers plus a parallel
 * `ADMIN_SETTINGS_TABS` id list that had to be kept in sync by hand. Both now
 * derive from this one descriptor array so a tab can be hidden in exactly one
 * place.
 *
 * `clientHidden` marks a tab that is irrelevant to a pure-client 3PL org (the
 * `isPureClient` verdict from `useOrgRoleSurfaces`). Hiding here is an
 * entitlement, not a user preference — unlike the production-issues tab strip
 * this list must NOT reveal a hidden tab just because it is the `?tab=` target.
 */
import {
  Building,
  FileText,
  Lock,
  MapPin,
  Package,
  Plug,
  Receipt,
  Terminal,
  Users,
  type LucideIcon,
} from 'lucide-react';

export interface SettingsTabDescriptor {
  id: string;
  /** i18n key under the `settings` namespace. */
  labelKey: string;
  icon: LucideIcon;
  /**
   * Hidden for pure-client orgs. Inventory settings holds only the master
   * inventory report, and the 3PL — not the client — manages the client's
   * stock, so the tab is dead weight on a client's Settings page. Billing is
   * hidden too: a pure client is never billed by PrintRun (its 3PL pays for
   * the pieces it prints, handover L3).
   */
  clientHidden?: boolean;
}

export const ADMIN_SETTINGS_TAB_DESCRIPTORS: readonly SettingsTabDescriptor[] = [
  { id: 'organization', labelKey: 'tabs.organization', icon: Building },
  { id: 'integrations', labelKey: 'tabs.integrations', icon: Plug },
  { id: 'purchase-orders', labelKey: 'tabs.purchaseOrders', icon: FileText },
  { id: 'inventory', labelKey: 'tabs.inventory', icon: Package, clientHidden: true },
  { id: 'locations', labelKey: 'tabs.locations', icon: MapPin },
  { id: 'users', labelKey: 'tabs.users', icon: Users },
  { id: 'security', labelKey: 'tabs.security', icon: Lock },
  { id: 'agent-api', labelKey: 'tabs.agentApi', icon: Terminal },
  // Last, so its slot is the one held by a skeleton while the org shape loads
  // and no other tab moves when it lands. Admin-only like every tab here (the
  // whole strip sits behind `org_settings.manage` in Settings.tsx).
  { id: 'billing', labelKey: 'tabs.billing', icon: Receipt, clientHidden: true },
] as const;

export const ADMIN_SETTINGS_TABS = ADMIN_SETTINGS_TAB_DESCRIPTORS.map(t => t.id);

/** Tabs a pure-client org may see. Pass `isPureClient: false` for owner orgs. */
export function computeVisibleSettingsTabs(isPureClient: boolean): SettingsTabDescriptor[] {
  return ADMIN_SETTINGS_TAB_DESCRIPTORS.filter(tab => !(isPureClient && tab.clientHidden));
}

/**
 * Resolve the tab to render. A `?tab=` value that names a hidden or unknown tab
 * falls back rather than being revealed — callers should also rewrite the URL so
 * it stops disagreeing with what is on screen.
 */
export function resolveActiveSettingsTab(
  requested: string | null,
  visible: readonly SettingsTabDescriptor[],
  fallback: string,
): string {
  if (requested && visible.some(tab => tab.id === requested)) return requested;
  return fallback;
}
